/**
 * Frontend type definitions for the backtesting platform.
 *
 * Core types (Candle, Order, Trade, Position, EquityPoint, etc.) are re-exported
 * from the backend Zod schemas in @/core/types so there is a single source of truth.
 * Frontend-only types (UI state, API request shapes, paper trading, etc.) are defined here.
 */

// ============================================================================
// Re-exports from core (single source of truth)
// ============================================================================

export type {
  Candle,
  Timeframe,
  OrderSide,
  OrderType,
  OrderStatus,
  Order,
  TradeAction,
  Trade,
  PositionSide,
  Position,
  EquityPoint,
  RollingMetrics,
  PerformanceMetrics,
  BacktestConfig,
  BacktestResult,
} from '@/core/types';

// ============================================================================
// Extended BacktestResult with API/client-side fields
// ============================================================================

import type { BacktestResult as CoreBacktestResult, Candle } from '@/core/types';

/**
 * BacktestResult extended with fields that are added either by the REST API
 * response or by the client-side backtest runner (useClientBacktest).
 *
 * - candles: the OHLCV bars used for this backtest run (not stored in core schema)
 * - duration: wall-clock execution time in milliseconds
 */
export interface ApiBacktestResult extends CoreBacktestResult {
  candles: Candle[];
  duration: number;
  /**
   * Phase 2: per-asset results when the result is an agg-of-one or aggregate run.
   * Present on results returned from the server (unified shape) and on results built
   * from the aggregate worker path.
   */
  perAssetResults?: Record<string, unknown>;
  /**
   * Phase 2: aggregation_config id linking this run to its agg-of-one or multi-asset run.
   */
  aggregationId?: string;
  /**
   * Phase 3.5: strategy_config id for single-strategy runs. Absent for portfolio runs.
   * Discriminator: strategyConfigId IS NOT NULL → single; aggregationId IS NOT NULL → portfolio.
   */
  strategyConfigId?: string;
}

// ============================================================================
// Strategy Types (frontend-only)
// ============================================================================

export type StrategyParamType = 'number' | 'string' | 'boolean' | 'select';

export interface StrategyParam {
  name: string;
  label: string;
  type: StrategyParamType;
  default: number | string | boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: string[] | Array<{ value: string | number; label: string }>;
  description?: string;
}

export interface StrategyInfo {
  name: string;
  description: string;
  version: string;
}

export interface StrategyDetails extends StrategyInfo {
  params: StrategyParam[];
  sourceFile: string;
}

// ============================================================================
// Backtest History / Summary
// ============================================================================

import type { Timeframe } from '@/core/types';

export interface BacktestSummary {
  id: string;
  strategyName: string;
  symbol: string;
  timeframe: Timeframe;
  totalReturnPercent: number;
  sharpeRatio: number;
  runAt: string;
  // Extended fields (optional for backward compatibility with old runs)
  exchange?: string;
  startDate?: number;
  endDate?: number;
  params?: Record<string, unknown>;
  maxDrawdownPercent?: number;
  winRate?: number;
  profitFactor?: number;
  totalTrades?: number;
  totalFees?: number;
  aggregationId?: string;
  aggregationName?: string;
  /**
   * Phase 3.5 discriminator: present for single-strategy runs (strategy_config_id IS NOT NULL).
   * Absent for portfolio/aggregation runs (aggregation_id IS NOT NULL).
   */
  strategyConfigId?: string;
  // BUG-20 run provenance — strategy version(s) at time of the run
  strategyVersions?: Record<string, number> | null;
}

export interface PaginatedHistory {
  results: BacktestSummary[];
  total: number;
  hasMore: boolean;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface CandleRequest {
  exchange: string;
  symbol: string;
  timeframe: Timeframe;
  startDate: string;
  endDate: string;
}

export interface RunBacktestRequest {
  strategyName: string;
  params: Record<string, unknown>;
  symbol: string;
  timeframe: Timeframe;
  startDate: string;
  endDate: string;
  initialCapital: number;
  exchange?: string;
}

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

// ============================================================================
// Optimization Types
// ============================================================================

import type { PerformanceMetrics } from '@/core/types';

export interface ParamRange {
  min: number;
  max: number;
  step: number;
}

export interface OptimizationRequest {
  strategyName: string;
  symbol: string;
  timeframe: Timeframe;
  startDate: string | number;
  endDate: string | number;
  initialCapital?: number;
  exchange?: string;
  paramRanges?: Record<string, ParamRange>;
  optimizeFor?: 'sharpeRatio' | 'totalReturnPercent' | 'profitFactor' | 'winRate' | 'sortinoRatio' | 'maxDrawdownPercent' | 'composite';
  minTrades?: number;
  maxCombinations?: number;
  batchSize?: number;
  saveAllRuns?: boolean;
  symbols?: string[];
  timeframes?: string[];
}

export interface OptimizationResult {
  id: string;
  strategyName: string;
  symbol: string;
  timeframe: string;
  bestParams: Record<string, unknown>;
  bestMetrics: PerformanceMetrics;
  totalCombinations: number;
  testedCombinations: number;
  optimizedAt: number;
  startDate?: number;
  endDate?: number;
  allResults?: Array<{
    params: Record<string, unknown>;
    metrics: PerformanceMetrics;
  }>;
}

export interface SubStrategyConfig {
  strategyName: string;
  symbol: string;
  timeframe: Timeframe;
  params?: Record<string, unknown>;
  exchange?: string;
}

export type AllocationMode = 'single_strongest' | 'weighted_multi' | 'top_n';

import type { Trade, EquityPoint, RollingMetrics } from '@/core/types';

export interface PerAssetResult {
  symbol: string;
  timeframe: string;
  trades: Trade[];
  equity: EquityPoint[];
  metrics: PerformanceMetrics;
  rollingMetrics?: RollingMetrics;
  fundingIncome: number;
  tradingPnl: number;
  /** Per-bar indicator data emitted by the strategy for this asset */
  indicators?: Record<string, { timestamps: number[]; values: number[] }>;
}

export interface AggregateBacktestResult extends ApiBacktestResult {
  perAssetResults?: Record<string, PerAssetResult>;
  aggregationId?: string;
  aggregationName?: string;
  signalHistory?: Array<{
    symbol: string;
    direction: 'long' | 'short' | 'flat';
    weight: number;
    strategyName: string;
    timestamp: number;
  }>;
}

// ============================================================================
// Aggregation Types (First-Class Entity)
// ============================================================================

export interface AggregationConfig {
  id: string;
  name: string;
  allocationMode: AllocationMode;
  maxPositions: number;
  subStrategies: SubStrategyConfig[];
  initialCapital: number;
  exchange: string;
  createdAt: number;
  updatedAt: number;
  subStrategyConfigIds?: string[];
  // BUG-20 versioning fields (WP3a — may be undefined on legacy responses)
  subStrategyVersions?: number[];
  subStrategyCurrentVersions?: (number | null)[];
  isStale?: boolean;
  // Portfolio version counter (portfolio_versions table; present on Wave 3b+ responses)
  currentVersion?: number;
}

export interface CreateAggregationRequest {
  name: string;
  allocationMode?: AllocationMode;
  maxPositions?: number;
  subStrategies: SubStrategyConfig[];
  initialCapital?: number;
  exchange?: string;
}

export interface UpdateAggregationRequest {
  name?: string;
  allocationMode?: AllocationMode;
  maxPositions?: number;
  subStrategies?: SubStrategyConfig[];
  initialCapital?: number;
  exchange?: string;
}

export interface RunAggregationRequest {
  startDate: string | number;
  endDate: string | number;
  initialCapital?: number;  // Override saved default
}

// ============================================================================
// UI State Types
// ============================================================================

export interface ChartMarker {
  time: number;
  position: 'aboveBar' | 'belowBar';
  color: string;
  shape: 'arrowUp' | 'arrowDown' | 'circle';
  text?: string;
}

// ============================================================================
// Scanner Types
// ============================================================================

export interface ScanRequest {
  strategy: string;
  symbols: string[];
  timeframe: string;
  from: string;
  to: string;
  slippage?: number;
  initialCapital?: number;
  params?: Record<string, unknown>;
}

export interface ScanResultMetrics {
  totalReturnPercent: number;
  sharpeRatio: number;
  maxDrawdownPercent: number;
  winRate: number;
  profitFactor: number;
}

export interface ScanResultRow {
  symbol: string;
  metrics: ScanResultMetrics;
  tradesCount: number;
  status: 'complete' | 'error';
  error?: string;
}

export interface ScanSummary {
  total: number;
  profitable: number;
  avgSharpe: number;
  avgReturn: number;
}

// ============================================================================
// Trade Display Helpers
// ============================================================================

import type { TradeAction } from '@/core/types';

/**
 * Check if a trade is an open action
 */
export function isOpenTrade(trade: Trade): boolean {
  return trade.action === 'OPEN_LONG' || trade.action === 'OPEN_SHORT';
}

/**
 * Check if a trade is a close action
 */
export function isCloseTrade(trade: Trade): boolean {
  return trade.action === 'CLOSE_LONG' || trade.action === 'CLOSE_SHORT';
}

/**
 * Get display label for trade action with arrow indicator
 * LONG = arrow up, SHORT = arrow down
 */
export function getTradeActionLabel(action: TradeAction): string {
  const labels: Record<TradeAction, string> = {
    'OPEN_LONG': 'Open Long ↑',
    'CLOSE_LONG': 'Close Long ↑',
    'OPEN_SHORT': 'Open Short ↓',
    'CLOSE_SHORT': 'Close Short ↓',
  };
  return labels[action];
}

/**
 * Get badge color class for trade action
 * OPEN = Green (entering a position)
 * CLOSE = Red (exiting a position)
 */
export function getTradeActionColor(action: TradeAction): string {
  switch (action) {
    case 'OPEN_LONG':
    case 'OPEN_SHORT':
      return 'bg-green-900/50 text-green-400';
    case 'CLOSE_LONG':
    case 'CLOSE_SHORT':
      return 'bg-red-900/50 text-red-400';
  }
}

// ============================================================================
// Paper Trading Types
// ============================================================================

export interface PaperSession {
  id: string;
  name: string;
  aggregationConfig: AggregationConfig;
  aggregationConfigId: string | null;
  strategyConfigId?: string | null;
  status: 'running' | 'paused' | 'stopped' | 'error';
  connectorType: 'paper' | 'bybit';
  initialCapital: number;
  currentEquity: number;
  currentCash: number;
  tickCount: number;
  lastTickAt: number | null;
  nextTickAt: number | null;
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;
  userId?: string;
  // --------------------------------------------------------------------------
  // Phase 1 Observability — bybit_reported_* columns (migration 041)
  // --------------------------------------------------------------------------
  /** Bybit-reported total account equity at last snapshot. null if not yet reconciled. */
  bybitReportedTotal: number | null;
  /** Bybit-reported available balance at last snapshot. null if not yet reconciled. */
  bybitReportedAvailable: number | null;
  /** Bybit-reported unrealised PnL at last snapshot. null if not yet reconciled. */
  bybitReportedUnrealizedPnl: number | null;
  /** Unix millisecond timestamp when the bybit_reported_* snapshot was taken. null if not yet reconciled. */
  bybitReportedAt: number | null;
  /** Per-session snapshot of the user's real Bybit taker fee rate. null for paper sessions or before first reconciliation. */
  feeRate: number | null;
  // BUG-20 versioning: snapshot of strategy versions at session-create time
  strategyVersionSnapshot?: Record<string, number> | null;
}

// ============================================================================
// BUG-20 Migrate Response Types
// ============================================================================

export interface MigrateStrategyConfigResponse {
  newConfigId: string;
  strategyVersion: number;
  dropped: string[];
  added: string[];
}

export interface MigrateAggregationPerSub {
  strategyName: string;
  dropped: string[];
  added: string[];
}

export interface MigrateAggregationResponse {
  newAggregationId: string;
  subStrategyVersions: number[];
  perSub: MigrateAggregationPerSub[];
}

export interface PaperPosition {
  id: number;
  sessionId: string;
  symbol: string;
  direction: 'long' | 'short';
  entryPrice: number;
  amount: number;
  entryTime: number;
  unrealizedPnl: number;
  fundingAccumulated: number;
  stopLoss: number | null;
  takeProfit: number | null;
  /**
   * Fix A: true when the sub-strategy params include a finite trailMult > 0.
   * Derived server-side; included in the position DTO from GET /sessions/:id.
   * Absent for legacy positions or when not provided by the server.
   */
  isTrailing?: boolean;
}

export interface PaperTrade {
  id: number;
  sessionId: string;
  symbol: string;
  action: 'open_long' | 'open_short' | 'close_long' | 'close_short';
  price: number;
  amount: number;
  timestamp: number;
  pnl: number | null;
  pnlPercent: number | null;
  fee: number;
  fundingIncome: number;
  balanceAfter: number;
}

export interface PaperEquitySnapshot {
  id: number;
  sessionId: string;
  timestamp: number;
  equity: number;
  cash: number;
  positionsValue: number;
}

export interface PaperSessionDetail extends PaperSession {
  positions: PaperPosition[];
}

export interface PaperTradesResponse {
  trades: PaperTrade[];
  total: number;
}

export type PaperTradingEvent =
  | { type: 'connected'; sessionId: string }
  | { type: 'trade_opened'; sessionId: string; trade: PaperTrade }
  | { type: 'trade_closed'; sessionId: string; trade: PaperTrade }
  | { type: 'funding_payment'; sessionId: string; symbol: string; amount: number; equity: number }
  | { type: 'equity_update'; sessionId: string; equity: number; cash: number; positionsValue: number; timestamp: number }
  | { type: 'tick_complete'; sessionId: string; tickNumber: number; timestamp: number; nextTickAt: number | null }
  | { type: 'error'; sessionId: string; message: string }
  | { type: 'status_change'; sessionId: string; oldStatus: string; newStatus: string }
  | {
      type: 'realtime_equity_update';
      sessionId: string;
      equity: number;
      cash: number;
      positionsValue: number;
      markPrices: Record<string, number>;
      timestamp: number;
      /**
       * Fix A: live trailing stop levels for open positions, keyed by symbol.
       * Emitted every 2s so the UI can update the stop display without a REST refetch.
       * Absent when no open positions have a trailing stop.
       */
      trailingStops?: Record<string, {
        side: 'long' | 'short';
        stopLoss: number | null;
        takeProfit: number | null;
        isTrailing: boolean;
      }>;
    };

export interface SimpleStrategyConfig {
  strategyName: string;
  symbol: string;
  timeframe: string;
  exchange: string;
  params: Record<string, unknown>;
}

export interface CreatePaperSessionRequest {
  name: string;
  aggregationConfigId?: string;
  strategyConfig?: SimpleStrategyConfig;
  backtestRunId?: string;
  strategyConfigId?: string;
  exchange?: string;
  initialCapital?: number;
  connectorType?: 'paper' | 'bybit';
  credentialId?: string | null;
  /** Leverage for position sizing (1 = no leverage, …, 125 = max). Default: 1. */
  leverage?: number;
  /**
   * SHA-28: slippage percent applied to simulated paper fills (e.g. 0.05 = 0.05%).
   * Omitted -> server resolves the realistic default (0.05% futures / 0% spot),
   * identical to prior paper-trading behavior. Not applicable to live trading.
   */
  slippagePercent?: number;
}

export interface PaperSessionEvent {
  id: number;
  sessionId: string;
  type: string;
  message: string;
  details: Record<string, unknown> | null;
  createdAt: number;
}

export interface PaperSessionEventsResponse {
  events: PaperSessionEvent[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Strategy Configuration Entity Types
// ============================================================================

export interface StrategyConfigEntity {
  id: string;
  strategyName: string;
  symbol: string;
  timeframe: string;
  params: Record<string, unknown>;
  contentHash: string;
  name: string;
  createdAt: number;
  // BUG-20 versioning fields (WP3a — may be undefined on legacy responses)
  strategyVersion?: number | null;
  strategyCurrentVersion?: number | null;
  isStale?: boolean;
}

export interface StrategyConfigListItem extends StrategyConfigEntity {
  runCount: number;
  paperSessionCount: number;
  latestRunAt?: number;
  latestRunSharpe?: number;
  latestRunReturn?: number;
}

// ============================================================================
// Platform Settings Types
// ============================================================================

export interface KillSwitchConfig {
  enabled: boolean;
  ddPercent: number;
}

export interface KillSwitchSettings {
  pt: KillSwitchConfig;
  lt: KillSwitchConfig;
}

export interface UpdateKillSwitchRequest {
  enabled: boolean;
  ddPercent?: number;
}

// ============================================================================
// User Strategy Types
// ============================================================================

export interface UserStrategy {
  id: string;
  userId: string;
  name: string;
  description: string;
  code: string;
  // esbuild-compiled JS (type annotations stripped); preferred for client-side execution
  compiledCode?: string;
  /** Semver string from the strategy code header (e.g. "1.0.0"). */
  version: string;
  /**
   * Monotonically-incrementing snapshot counter stored in user_strategies.current_version.
   * Matches the integer version numbers in user_strategy_versions.version.
   * Use this (not `version`) when comparing against StrategyVersionSummary.version.
   */
  currentVersion?: number;
  params: StrategyParam[];
  createdAt: number;
  updatedAt: number;
  configCount?: number;
}

// ============================================================================
// Credentials Types
// ============================================================================

export interface CredentialSummary {
  id: string;
  label: string;
  exchange: string;
  testnet: boolean;
  maskedKey: string;
  createdAt: string;
}

export interface CreateCredentialRequest {
  label: string;
  exchange: string;
  apiKey: string;
  apiSecret: string;
  testnet?: boolean;
}

export type TestCredentialResult =
  | { success: true; balance: { total: number; available: number } }
  | { success: false; error: string };

// ============================================================================
// API Token Types
// ============================================================================

/** Summary row returned by GET /api/auth/api-tokens (token value is never included) */
export interface ApiTokenSummary {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

/** Response body returned once after POST /api/auth/api-tokens (full token shown once) */
export interface ApiTokenCreated {
  id: string;
  token: string;
  prefix: string;
  name: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface CreateApiTokenRequest {
  name: string;
}

// ============================================================================
// Auth User (with onboarding fields)
// ============================================================================

/** Full user object returned by GET /api/auth/me */
export interface AuthUser {
  id: string;
  username: string;
  role: string;
  onboardingCompleted: boolean;
  onboardingCurrentStep: number;
  termsAcceptedAt: number | null;
  onboardingCompletedAt: number | null;
}

export interface UpdateOnboardingRequest {
  currentStep?: number;
  completed?: boolean;
  acceptedTerms?: boolean;
}
